/**
 * Published release notes, newest first.
 *
 * To publish a new release note, add an entry at the top of RELEASE_NOTES.
 * The first entry is featured as "Latest" on /whats-new; the rest become the history.
 *
 * Keep entries short: one line per change, enough to know what is new.
 * The long form lives in the workspace docs folder, e.g. docs/RELEASE_NOTES_SCHOOL_2026-09.md.
 */

export interface ReleaseNoteItem {
  /** Optional bold lead-in, e.g. "Sync your school from Wonde." */
  lead?: string;
  text: string;
}

export interface ReleaseNoteSection {
  title: string;
  items: ReleaseNoteItem[];
}

export interface ReleaseNote {
  /** Stable id, also used as the anchor on the page. */
  id: string;
  title: string;
  /** Release date, ISO (yyyy-MM-dd). Displayed with the date pipe. */
  releasedOn: string;
  summary: string;
  sections: ReleaseNoteSection[];
}

export const RELEASE_NOTES: ReleaseNote[] = [
  {
    id: '2026-10',
    title: 'October 2026',
    releasedOn: '2026-10-06',
    summary:
      'See which pupils are actually protected during a restriction, review MIS sync mismatches in one decision, approve parent unlink requests, and find answers in the new Help & FAQ.',
    sections: [
      {
        title: 'Restriction enforcement',
        items: [
          {
            lead: 'Know who is really protected.',
            text: 'Each restriction shows how many pupils were confirmed protected, not confirmed, or had no device, with the pupils behind each count.',
          },
          { text: 'The day board breaks the same counts down by year group, with a search over each group of pupils.' },
          { text: "Today's restrictions on the home page show the same three counts." },
          { text: 'Device coverage shows how many pupils could be restricted when a restriction started, and why the others could not.' },
          { text: 'A phone that did not answer is never reported as an unprotected pupil.' },
        ],
      },
      {
        title: 'Restrictions',
        items: [
          { text: "Restriction times are shown and edited in the restriction's own time zone." },
          { text: 'The board shows the real finish time when a school calendar exception ends a restriction early.' },
        ],
      },
      {
        title: 'MIS integration',
        items: [
          {
            lead: 'Review sync mismatches.',
            text: 'Pupils the sync could not match to exactly one pupil are listed for review. Pick the existing pupil and choose which details to update, or add them as a new pupil.',
          },
          { text: 'The sync carries on if you leave the page, and you find its progress or last summary when you come back.' },
          { text: 'A notice in your inbox tells you when a sync has finished.' },
        ],
      },
      {
        title: 'Pupils and groups',
        items: [
          { text: 'A pupil shows as inactive until one of their devices is enrolled.' },
          {
            text: 'Pupils left in no active group are flagged, with a filter and a Move to group action. Deleting a group warns you first if it would leave pupils behind.',
          },
          { text: 'Search users by full name, in either order.' },
          { text: 'Group and pupil picker tables can be sorted.' },
          { text: 'Family details are optional when adding a pupil.' },
        ],
      },
      {
        title: 'Parent requests',
        items: [
          { text: 'Parents asking to unlink a child from the school now need your approval, and each request says whether it is a removal or an unlink.' },
        ],
      },
      {
        title: 'Help',
        items: [
          {
            lead: 'Help & FAQ.',
            text: 'A searchable guide to every part of the School App, also reachable from Help buttons on the pages that need them.',
          },
          { text: 'Responses to Parents gives ready to send replies to common parent questions, with a Copy button.' },
          { text: 'Contact us shows our address with a copy button and links to open your mail.' },
        ],
      },
      {
        title: 'Fixes',
        items: [
          { text: 'Dates are shown as dd/MM/yyyy everywhere.' },
          { text: 'List pages no longer jump back to the first page after you change page.' },
          { text: 'Group search no longer breaks on groups without a description.' },
          { text: 'Small enrolment figures show a decimal instead of 0%.' },
          { text: 'Staff whose password storage was upgraded are told to set a new one with Forgot password.' },
        ],
      },
    ],
  },
  {
    id: '2026-09',
    title: 'September 2026',
    releasedOn: '2026-09-12',
    summary:
      'A school calendar that pauses restrictions on holidays and closures, part-time pupils and exemptions, parent requests you can review in the app, a notification inbox kept on the server, and a refreshed LOQT look across every screen.',
    sections: [
      {
        title: 'School calendar',
        items: [
          {
            lead: 'Holidays, closures and trips now pause restrictions.',
            text: 'Record school and bank holidays, INSET days, closures and trips, and restrictions are suspended for those days.',
          },
          { text: 'No more cancelling a restriction one day at a time. Pupils are not locked when the school is closed.' },
          { text: 'An exception that begins during the day ends the restriction for the rest of that day.' },
        ],
      },
      {
        title: 'Pupils',
        items: [
          {
            lead: 'Part-time pupils.',
            text: 'Set the week a pupil actually attends. On a day they do not attend, no school restriction is applied.',
          },
          {
            text: 'Exempt a pupil from school restrictions, with a reason. The exemption is visible on the pupil record and in the parent app.',
          },
          {
            text: "Pupil name, date of birth and gender now belong to the school. A parent editing their child's profile no longer changes your record.",
          },
        ],
      },
      {
        title: 'Parent requests',
        items: [
          {
            text: "Review pupil link requests when a parent's child cannot be matched to one pupil, then link the right pupil or create a new one.",
          },
          { text: 'Approve or decline a parent asking to remove a school-linked child, with the reason they gave.' },
          { text: 'Requests are grouped by pupil, so one family is handled in one go.' },
        ],
      },
      {
        title: 'Notifications',
        items: [
          { text: 'The notification bell and history are now kept on the server, so what you have read follows you between browsers.' },
          { text: 'Choose which staff receive parent cancellation emails, in Settings.' },
          { text: 'Opening a notice takes you to the pupil or request it refers to.' },
        ],
      },
      {
        title: 'Restrictions',
        items: [
          { text: 'A week board view of the restriction list, with a toggle for weekends.' },
          { text: 'Cancelling or editing a single day now updates the list straight away instead of flickering back.' },
          { text: 'Fixed: restrictions started an hour earlier than set.' },
          { text: 'Fixed: in large year groups, some pupils never received the start or unlock notification.' },
          { text: 'Duplicate rows left by a data sync are gone, and restrictions left open after a service interruption now close themselves.' },
        ],
      },
      {
        title: 'A refreshed look',
        items: [
          { text: 'The LOQT identity across every screen, one page header everywhere, and a clearer profile page.' },
          {
            lead: 'Add your school photo.',
            text: 'It appears in your header, and on the child profile in the parent app.',
          },
          { text: 'The same search and filter bar on every list page, and a tidier device list.' },
        ],
      },
      {
        title: 'School data integration',
        items: [
          { text: 'A pupil can be updated when an inactive user happens to share their name.' },
          {
            text: 'Sync no longer creates a second record for a pupil already on the roster, and skips pupils whose details have not changed.',
          },
          { text: 'Settings flags when a data audit is needed before synchronising, and the emails sent after approval were rewritten.' },
        ],
      },
      {
        title: 'Accounts and security',
        items: [
          { text: 'Fixed: creating a school account sent two verification emails and rejected valid school codes.' },
          { text: 'School sessions are now version checked, so a session that has been signed out cannot be refreshed back into use.' },
        ],
      },
      {
        title: 'Technical notes',
        items: [
          { text: 'Unit test coverage gates in CI for both the school app and the services.' },
          { text: 'Backfills for the notification inbox, pupil status, duplicate children and devices left mid-enrolment.' },
        ],
      },
    ],
  },
  {
    id: '2026-08',
    title: 'August 2026',
    releasedOn: '2026-08-30',
    summary:
      'School data sync from Wonde, school tasks that follow attendance, clearer restriction edits and much faster user and group lists.',
    sections: [
      {
        title: 'School data integration',
        items: [
          {
            text: 'Sync your school, pupils, staff and classes from Wonde, on demand from Settings, then School Profile.',
          },
          { text: 'The sync reports what was created, updated and failed, with any provider errors.' },
          { text: 'Pupils arrive already linked to their classes.' },
        ],
      },
      {
        title: 'School tasks and restrictions',
        items: [
          { text: 'School tasks are cancelled automatically for pupils marked absent.' },
          {
            text: 'Editing a repeating restriction now asks for the scope: this occurrence, from a date, or all of them.',
          },
          { text: 'Cancelling one day cancels that day only, and the list updates straight away.' },
          { text: 'Restrictions that never started show as Skipped instead of staying Upcoming.' },
        ],
      },
      {
        title: 'Device oversight',
        items: [
          { text: 'Enrolled devices that stop checking in are flagged in the device list.' },
          {
            text: "Parents are alerted when protection is switched off on a pupil's device. A device silent for 60 days has to be paired again.",
          },
          { text: 'Devices report their real model and operating system version.' },
        ],
      },
      {
        title: 'Speed and reliability',
        items: [
          { text: 'User and group lists no longer time out on large synced rosters.' },
          { text: 'Devices and groups load from the cache first and refresh in the background.' },
          { text: 'Steadier sessions, and no more intermittent errors while validating a code.' },
        ],
      },
      {
        title: 'Accounts and security',
        items: [
          { text: 'Parents can sign up and log in with an email address.' },
          {
            text: 'New risk checks block automated account creation, and two sign-ups with the same email or phone can no longer race each other.',
          },
          { text: 'Text messages are signed, capped per number, and consent is recorded at sign-up.' },
        ],
      },
      {
        title: 'Parent app',
        items: [
          {
            text: "A device protection screen shows whether a child's device is protected and when it next checks in.",
          },
          {
            text: 'Repeating tasks can be edited by occurrence or skipped, with redesigned account and family screens.',
          },
        ],
      },
      {
        title: 'Technical notes',
        items: [
          { text: 'New integration service with a provider-agnostic interface, Wonde first.' },
          { text: 'Axon messages moved from XML to Jackson serialization.' },
          { text: 'School listing queries batched and indexed, and service memory limits raised.' },
          {
            text: 'Android 16 target, hardened iOS background enforcement, and CI split into install, build and test with coverage thresholds.',
          },
        ],
      },
    ],
  },
];

/** Title of the newest release, or an empty string when nothing has been published yet. */
export function getLatestReleaseTitle(notes: ReleaseNote[] = RELEASE_NOTES): string {
  return notes.length > 0 ? notes[0].title : '';
}
